/**
 * Node visual para Set Variable
 * Exibe atribuição de valor a variável no canvas
 * Inclui toolbar flutuante ao selecionar
 */

import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Variable } from 'lucide-react';
import { FlowNodeToolbar } from './FlowNodeToolbar';

interface SetVariableNodeProps {
  data: {
    type: string;
    label: string;
    config?: {
      variable_name?: string;
      value_type?: string;
      value?: string;
    };
    onEdit?: () => void;
    onDelete?: () => void;
  };
  id: string;
  selected?: boolean;
}

/**
 * Componente visual do node Set Variable
 */
export const SetVariableNode = memo(({ data, id, selected }: SetVariableNodeProps) => {
  const config = data.config || {};
  const variableName = config.variable_name || 'variável';
  const valueType = config.value_type || 'static';
  const value = config.value || '';

  // Callbacks injetados via FlowCanvas
  const onEdit = data.onEdit;
  const onDuplicate = (data as any).onDuplicate;
  const onDelete = data.onDelete;
  const onTestFromHere = (data as any).onTestFromHere;

  // Trunca valor longo para exibição
  const displayValue = value.length > 30 ? value.substring(0, 30) + '...' : value;

  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-cyan-500 bg-card min-w-[200px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={data.type || 'set_variable'}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-cyan-500" />
      
      <div className="flex items-start gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-cyan-500 text-white flex-shrink-0">
          <Variable className="h-4 w-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-1">{(data.config as any)?.nickname || 'Set Variable'}</div>
          
          {variableName && (
            <div className="text-xs space-y-1">
              <div className="font-mono bg-muted px-2 py-1 rounded">
                <span className="text-cyan-600 dark:text-cyan-400 font-semibold">
                  {variableName}
                </span>
                {' = '}
                <span className="text-muted-foreground break-all">
                  {valueType === 'variable' ? `{{${displayValue}}}` : `"${displayValue}"`}
                </span>
              </div>
              
              <div className="text-muted-foreground">
                Tipo: {
                  valueType === 'static' ? 'Estático' :
                  valueType === 'variable' ? 'Variável' :
                  'Expressão'
                }
              </div>
            </div>
          )}
          
          {!variableName && (
            <div className="text-xs text-muted-foreground italic">
              Clique para configurar
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-cyan-500" />
    </div>
  );
});

SetVariableNode.displayName = 'SetVariableNode';
